import {
    TbBriefcase,
    TbBuildingFactory,
    TbBuildingFactory2,
    TbBuildingWarehouse,
    TbCategory,
    TbCircleX,
    TbMap2,
    TbMapPin,
    TbMapPins,
    TbPackage,
    TbSitemap,
    TbTool,
    TbTruck,
    TbTruckLoading,
    TbUsersGroup,
} from 'react-icons/tb'

import type { ListResourceMenuItem } from '../Hooks/useListResources'
import type { IconType } from 'react-icons'

/** Ручна прив'язка довідника до групи: ключ ресурсу + іконка. Порядок = порядок показу. */
export interface DictionaryGroupConfig {
    key: string
    label: string
    items: Array<{ resourceKey: string; icon: IconType }>
}

/** Довідник з даними ресурсу (назва, шляхи) і іконкою з реєстру */
export interface DictionaryItem {
    resourceKey: string
    title: string
    pageUrl: string
    restUrl: string
    icon: IconType | null
}

export interface DictionaryGroup {
    key: string
    label: string
    items: DictionaryItem[]
}

/** Група для довідників, яких немає в реєстрі: щоб новий ресурс не зник з розділу */
export const OTHER_GROUP_KEY = 'other'

/**
 * Групи виведені зі зв'язків у бекенді (хто посилається на довідник):
 * транспорт — VehicleEntity/VehicleNeed; об'єкти — LoadingPoint/Request/Assignment;
 * заявки — TransportRequest/VehicleNeed; організація — TransportRequest.organizationUnit;
 * географія — Region.cluster (регіон належить кластеру), ClusterResponsible.cluster.
 * Новий довідник додається сюди; тест dictionaries.test.ts нагадає, якщо забути.
 */
export const DICTIONARY_GROUPS: DictionaryGroupConfig[] = [
    {
        key: 'transport',
        label: 'Транспорт',
        items: [
            { resourceKey: 'Setting.ListVehicleKind', icon: TbTruck },
            { resourceKey: 'Setting.ListVehicleType', icon: TbTruckLoading },
            { resourceKey: 'Setting.ListCarrier', icon: TbBuildingWarehouse },
        ],
    },
    {
        key: 'objects',
        label: "Об'єкти та пункти",
        items: [
            { resourceKey: 'Setting.ListConstructionObject', icon: TbBuildingFactory },
            { resourceKey: 'Setting.ListProductionObject', icon: TbBuildingFactory2 },
            { resourceKey: 'Setting.ListObjectType', icon: TbCategory },
            { resourceKey: 'Setting.ListLoadingPoint', icon: TbMapPin },
        ],
    },
    {
        key: 'requests',
        label: 'Заявки',
        items: [
            { resourceKey: 'Setting.ListCargo', icon: TbPackage },
            { resourceKey: 'Setting.ListTechOperation', icon: TbTool },
            { resourceKey: 'Setting.ListCancellationReason', icon: TbCircleX },
        ],
    },
    {
        key: 'organization',
        label: 'Організація',
        items: [
            { resourceKey: 'Setting.ListOrganizationUnit', icon: TbSitemap },
            { resourceKey: 'Setting.ListPosition', icon: TbBriefcase },
        ],
    },
    {
        key: 'geography',
        label: 'Географія',
        items: [
            { resourceKey: 'Setting.ListCluster', icon: TbMap2 },
            { resourceKey: 'Setting.ListRegion', icon: TbMapPins },
            { resourceKey: 'Setting.ListClusterResponsible', icon: TbUsersGroup },
        ],
    },
]

/**
 * Розкладає доступні ресурси `Setting.List*` по групах реєстру. Групи без жодного
 * доступного ресурсу пропускаються; ресурси поза реєстром — у групі «Інше» в кінці.
 */
export const resolveDictionaryGroups = (resources: ListResourceMenuItem[]): DictionaryGroup[] => {
    const byKey = new Map(resources.map((resource) => [resource.key, resource]))
    const placed = new Set<string>()

    const toItem = (resource: ListResourceMenuItem, icon: IconType | null): DictionaryItem => ({
        resourceKey: resource.key,
        title: resource.title,
        pageUrl: resource.pageUrl,
        restUrl: resource.restUrl,
        icon,
    })

    const groups: DictionaryGroup[] = []
    DICTIONARY_GROUPS.forEach((group) => {
        const items: DictionaryItem[] = []
        group.items.forEach(({ resourceKey, icon }) => {
            const resource = byKey.get(resourceKey)
            if (!resource) return
            placed.add(resourceKey)
            items.push(toItem(resource, icon))
        })
        if (items.length > 0) groups.push({ key: group.key, label: group.label, items })
    })

    const other = resources.filter((resource) => !placed.has(resource.key)).map((resource) => toItem(resource, null))
    if (other.length > 0) groups.push({ key: OTHER_GROUP_KEY, label: 'Інше', items: other })

    return groups
}
